import type { GetBudgetServicesV2Params } from "../types/budget/budget-service-v2";

/**
 * Cache filters for `GET /v2/budget/:id/services` (FE-P2-02 / Phase 3 prep).
 * Group key = `calcOption` + `category` + `supplierId`; `page`/`limit` are pagination.
 */
export type BudgetServicesQueryFilters = GetBudgetServicesV2Params;

export const queryKeys = {
  profile: ["profile"] as const,
  usersAll: ["users", "all"] as const,
  cellsAll: ["cells", "all"] as const,
  clients: (params?: Record<string, unknown>) =>
    ["clients", params ?? {}] as const,
  projects: (filters: unknown) => ["projects", filters] as const,
  /** Indicadores da Tela de Início; sem escopo = padrão do perfil. */
  homeSummary: (scope?: string) =>
    ["home-summary", scope ?? "default"] as const,
  /** Fases, carga e movimentações do Início (TASK 2). */
  homeTracking: (scope?: string) =>
    ["home-tracking", scope ?? "default"] as const,
  homeCoordinatorProjects: (scope: string, coordinator: string) =>
    ["home-coordinator-projects", scope, coordinator] as const,
  /** Dashboard "Tempo e Eficiência dos Projetos" ao vivo (#MMV-2615). */
  timeEfficiencyLiveOptions: ["time-efficiency-live-options"] as const,
  timeEfficiencyLiveKpis: (filters: unknown) =>
    ["time-efficiency-live-kpis", filters] as const,
  timeEfficiencyLiveProjects: (filters: unknown) =>
    ["time-efficiency-live-projects", filters] as const,
  timeEfficiencyLiveStageChart: (filters: unknown) =>
    ["time-efficiency-live-stage-chart", filters] as const,
  timeEfficiencyLiveStaleProjects: (filters: unknown) =>
    ["time-efficiency-live-stale-projects", filters] as const,
  timeEfficiencyLiveMonthlyEvolution: [
    "time-efficiency-live-monthly-evolution",
  ] as const,
  timeEfficiencyLiveProjectsAboveAverage: (filters: unknown) =>
    ["time-efficiency-live-projects-above-average", filters] as const,
  timeEfficiencyLiveReopenKpi: (filters: unknown) =>
    ["time-efficiency-live-reopen-kpi", filters] as const,
  timeEfficiencyLiveProjectsWithReopening: (filters: unknown) =>
    ["time-efficiency-live-projects-with-reopening", filters] as const,
  timeEfficiencyLiveRankings: (filters: unknown) =>
    ["time-efficiency-live-rankings", filters] as const,
  timeEfficiencyLiveProjectHistory: (projectId: string) =>
    ["time-efficiency-live-project-history", projectId] as const,
  projectSummaries: (projectId: string) =>
    ["project-summaries", projectId] as const,
  budgetList: (projectId: string) => ["budget-list", projectId] as const,
  budgetSummaryList: (projectId: string) =>
    ["budget-summary-list", projectId] as const,
  /** Hydrated full budget by id (editor detail cache for FE-P1-03). */
  budgetDetail: (budgetId: string) => ["budget-detail", budgetId] as const,
  /** Optional v2 shell cache (`GET /v2/budget/:id`). */
  budgetShell: (budgetId: string) => ["budget-shell", budgetId] as const,
  /**
   * V2 service list cache (`GET /v2/budget/:id/services`).
   * Used by FE-P3-01 editor bootstrap (page drain) and later FE-P3-02 group loads.
   *
   * @example
   * queryKeys.budgetServices(budgetId, { calcOption: "A", category: "Audio", supplierId })
   * // → ["budget-services", budgetId, { calcOption, category, supplierId }]
   */
  budgetServices: (
    budgetId: string,
    filters: BudgetServicesQueryFilters = {}
  ) => ["budget-services", budgetId, filters] as const,
  /**
   * Per-service `updatedAt` ISO map for If-Match (FE-P3-05).
   * Seeded on v2 detail bootstrap; not part of the editor `ServiceData` allowlist.
   */
  budgetServiceUpdatedAts: (budgetId: string) =>
    ["budget-service-updated-ats", budgetId] as const,
  /**
   * Server totals slice cache (`GET /v2/budget/:id/totals`, FE-P4-01).
   * Keyed by phase (+ negotiationVersion / calcOption when present).
   */
  budgetTotals: (
    budgetId: string,
    filters: {
      phase: string;
      negotiationVersion?: number;
      calcOption?: string;
    }
  ) => ["budget-totals", budgetId, filters] as const,
  projectLastEditorAudit: (projectId: string) =>
    ["audit", "project-last-editor", projectId] as const,
  projectTimelineAudit: (projectId: string) =>
    ["audit", "project-timeline", projectId] as const,
};
